
import { useState, useCallback, useEffect } from 'react';
import { api, getApiBaseUrl } from '@/lib/api';
import { PrayerRequest, PrayerStatus, PaginatedResponse } from '@/types';

export interface ArchiveFilters {
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

interface UseArchiveReturn {
  prayers: PrayerRequest[];
  isLoading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  totalItems: number;
  filters: ArchiveFilters;
  setFilters: (filters: ArchiveFilters) => void;
  setPage: (page: number) => void;
  fetchPrayers: () => Promise<void>;
  archivePrayer: (id: string) => Promise<boolean>;
  bulkArchive: (olderThanDays: number) => Promise<{ archivedCount: number } | null>;
  exportToCsv: () => Promise<Blob | null>;
}

export function useArchive(): UseArchiveReturn {
  const [prayers, setPrayers] = useState<PrayerRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<ArchiveFilters>({});

  const fetchPrayers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('skip', String((page - 1) * pageSize));
      params.append('limit', String(pageSize));
      if (filters.status) params.append('status', filters.status);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get<PaginatedResponse<PrayerRequest>>(
        `/admin/prayers/all?${params.toString()}`
      );
      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        setPrayers(response.data.items);
        setTotalItems(response.data.total);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch prayers');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, filters]);

  const archivePrayer = useCallback(
    async (id: string): Promise<boolean> => {
      setError(null);
      try {
        const response = await api.put<PrayerRequest>(
          `/prayer-requests/${id}`,
          { status: PrayerStatus.ARCHIVED }
        );
        if (response.error) {
          setError(response.error);
          return false;
        }
        setPrayers((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, status: PrayerStatus.ARCHIVED } : p
          )
        );
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to archive prayer');
        return false;
      }
    },
    []
  );

  const bulkArchive = useCallback(
    async (
      olderThanDays: number
    ): Promise<{ archivedCount: number } | null> => {
      setError(null);
      try {
        const response = await api.post<{ archived_count: number }>(
          '/admin/prayers/archive',
          { older_than_days: olderThanDays }
        );
        if (response.error) {
          setError(response.error);
          return null;
        }
        // Refresh the list after bulk archive
        await fetchPrayers();
        return { archivedCount: response.data?.archived_count ?? 0 };
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to bulk archive');
        return null;
      }
    },
    [fetchPrayers]
  );

  const exportToCsv = useCallback(async (): Promise<Blob | null> => {
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.search) params.append('search', filters.search);
      params.append('format', 'csv');

      const response = await fetch(
        `${getApiBaseUrl()}/admin/prayers/export?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        }
      );

      if (!response.ok) {
        setError('Failed to export data');
        return null;
      }

      return await response.blob();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export');
      return null;
    }
  }, [filters]);

  // Fetch prayers when page or filters change
  useEffect(() => {
    fetchPrayers();
  }, [fetchPrayers]);

  return {
    prayers,
    isLoading,
    error,
    page,
    pageSize,
    totalItems,
    filters,
    setFilters,
    setPage,
    fetchPrayers,
    archivePrayer,
    bulkArchive,
    exportToCsv,
  };
}
